import { useInvoiceReportsSearchRefetch } from '@/components/InvoiceReportDetailDrawer/context/InvoiceReportsSearchRefetchContext';
import { useInvoiceReportApprovalRoute } from '@/context/services/invoice_reports/InvoiceReportApprovalRoute.service';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect } from 'react';

type UseApprovalRouteProps = {
  reportId: string;
  onCloseDetailDrawer: () => void;
};

export const useApprovalRoute = ({
  reportId,
  onCloseDetailDrawer,
}: UseApprovalRouteProps) => {
  const refetch = useInvoiceReportsSearchRefetch();
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const { data, error } = useInvoiceReportApprovalRoute(reportId);

  const showErrorMessages = useCallback(
    (messages?: string | string[]) => {
      const title = Array.isArray(messages)
        ? messages.join(',')
        : messages || t('system_error');
      notify.error({ title, duration: 3 });
    },
    [notify, t]
  );

  const onCheckAccessSuccess = useCallback(() => {
    onCloseDetailDrawer();
    showErrorMessages(error?.messages);
  }, [onCloseDetailDrawer, error?.messages, showErrorMessages]);

  useEffect(() => {
    if (!error) return;
    if (error.code === '401' || error.code === '403') {
      handleCheckPermission({
        fnCheckPermission: refetch,
        onCheckSuccess: onCheckAccessSuccess,
        onCheckFailure: onCloseDetailDrawer,
      });
    } else {
      showErrorMessages(error.messages);
      onCloseDetailDrawer();
    }
  }, [
    error,
    onCloseDetailDrawer,
    refetch,
    handleCheckPermission,
    showErrorMessages,
    onCheckAccessSuccess,
  ]);

  return { data };
};
